import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { authAxios } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { RefreshCw, Send, CheckCircle, Plus, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import AIPendingDraftsPanel from '../../components/AIPendingDraftsPanel';

export default function AdminEventLog() {
  const { user, currentSeason, API } = useContext(AppContext);
  const [aiFeatureEnabled, setAiFeatureEnabled] = useState(false);
  const [events, setEvents] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [contestants, setContestants] = useState([]);
  const [tribes, setTribes] = useState([]);
  const [users, setUsers] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [scoringRules, setScoringRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showBulkEvent, setShowBulkEvent] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState('all');
  const [editingEvent, setEditingEvent] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  
  // Bulk selection state
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    episode_id: '',
    player_user_id: '',
    contestant_id: '',
    tribe_id: '',
    event_name: '',
    points: '',
    notes: ''
  });
  
  const [newEvent, setNewEvent] = useState({
    episode_id: '',
    user_id: '',
    contestant_id: '',
    tribe_id: '',
    event_name: '',
    points: '',
    notes: ''
  });
  
  const [bulkEvent, setBulkEvent] = useState({
    episode_id: '',
    tribe_id: '',
    event_name: '',
    notes: ''
  });

  // Bulk add mode: 'tribe' or 'contestants'
  const [bulkAddMode, setBulkAddMode] = useState('tribe');
  const [selectedContestants, setSelectedContestants] = useState([]);
  
  // Import events state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Show non-active contestants toggle (persisted to localStorage)
  const [showInactiveContestants, setShowInactiveContestants] = useState(() => {
    const saved = localStorage.getItem('eventlog_show_inactive_contestants');
    return saved === 'true';
  });
  
  // Handle toggle change and persist to localStorage
  const handleShowInactiveToggle = (checked) => {
    setShowInactiveContestants(checked);
    localStorage.setItem('eventlog_show_inactive_contestants', String(checked));
  };
  
  // Filter contestants based on active status
  const inactiveStatuses = ['voted_out', 'removed', 'jury'];
  const activeContestants = contestants.filter(c => !inactiveStatuses.includes(c.status));
  const displayedContestants = showInactiveContestants ? contestants : activeContestants;
  
  // Filter tribes that have at least one active member
  const tribesWithActiveMembers = tribes.filter(tribe => {
    const tribeContestants = contestants.filter(c => c.tribe_id === tribe.id);
    return tribeContestants.some(c => !inactiveStatuses.includes(c.status));
  });
  const displayedTribes = showInactiveContestants ? tribes : tribesWithActiveMembers;

  useEffect(() => {
    if (currentSeason) {
      loadData();
      // Load AI feature flag
      authAxios.get(`${API}/admin/league-options`).then(({ data }) => {
        setAiFeatureEnabled(!!data?.ai_event_drafting_enabled);
      }).catch(() => setAiFeatureEnabled(false));
    }
  }, [currentSeason]);

  const recalculateScores = async () => {
    try {
      await authAxios.post(
        `${API}/seasons/${currentSeason.id}/recalculate`,
        {},
        { withCredentials: true }
      );
      console.log('✓ Scores recalculated automatically');
    } catch (error) {
      console.error('Failed to auto-recalculate scores:', error);
      // Don't show error to user - this is a background operation
    }
  };

  const finalizeEpisodeScores = async () => {
    if (selectedEpisode === 'all') {
      toast.error('Please select a specific episode to finalize');
      return;
    }
    
    try {
      setFinalizing(true);
      const response = await authAxios.post(
        `${API}/seasons/${currentSeason.id}/finalize-episode`,
        { episode_id: selectedEpisode }
      );
      
      const epNum = episodes.find(e => e.id === selectedEpisode)?.number || '?';
      toast.success(
        `Episode ${epNum} finalized! ${response.data.players_notified} players notified.`,
        { duration: 5000 }
      );
      setShowFinalizeDialog(false);
    } catch (error) {
      console.error('Failed to finalize episode:', error);
      toast.error(error.response?.data?.detail || 'Failed to finalize episode scores');
    } finally {
      setFinalizing(false);
    }
  };

  const backfillPlayerAssignments = async () => {
    try {
      const response = await authAxios.post(
        `${API}/seasons/${currentSeason.id}/event-log/backfill-players`
      );
      
      if (response.data.updated > 0) {
        toast.success(`Updated ${response.data.updated} events with player assignments!`);
        // After backfill, automatically recalculate scores
        await recalculateScoresOnly();
        loadData(); // Reload to show updated data
      } else {
        toast.info('All events already have player assignments');
      }
    } catch (error) {
      console.error('Failed to backfill players:', error);
      toast.error(error.response?.data?.detail || 'Failed to backfill player assignments');
    }
  };

  const recalculateScoresOnly = async () => {
    try {
      const response = await authAxios.post(
        `${API}/seasons/${currentSeason.id}/recalculate`
      );
      toast.success(`Scores recalculated! ${response.data.scorecard_count} scorecard entries, ${response.data.leaderboard_count} leaderboard entries.`);
      return response.data;
    } catch (error) {
      console.error('Failed to recalculate scores:', error);
      toast.error(error.response?.data?.detail || 'Failed to recalculate scores');
      throw error;
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [eventsRes, episodesRes, contestantsRes, tribesRes, rulesRes, usersRes, rostersRes] = await Promise.all([
        authAxios.get(`${API}/seasons/${currentSeason.id}/event-log`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/episodes`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/contestants`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/tribes`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/scoring-rules`),
        authAxios.get(`${API}/users/list`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/rosters`)
      ]);
      
      setEvents(eventsRes.data);
      setEpisodes(episodesRes.data);
      setContestants(contestantsRes.data);
      setTribes(tribesRes.data);
      setScoringRules(rulesRes.data);
      setUsers(usersRes.data);
      setRosters(rostersRes.data);
      
      // Debug log to check user color data
      console.log('AdminEventLog - Loaded users with colors:', usersRes.data.map(u => ({ name: u.name, color: u.color })));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get contestants for a specific user
  const getContestantsForUser = (userId) => {
    const userRosters = rosters.filter(r => r.user_id === userId && r.is_active);
    return userRosters.map(r => contestants.find(c => c.id === r.contestant_id)).filter(Boolean);
  };

  // Get user for a contestant - returns single user (legacy, for backwards compatibility)
  const getUserForContestant = (contestantId) => {
    const roster = rosters.find(r => r.contestant_id === contestantId && r.is_active);
    if (roster) {
      return users.find(u => u.id === roster.user_id);
    }
    return null;
  };

  // Get all users for a contestant (for double draft support)
  const getUsersForContestant = (contestantId) => {
    const contestantRosters = rosters.filter(r => r.contestant_id === contestantId && r.is_active !== false);
    return contestantRosters.map(r => users.find(u => u.id === r.user_id)).filter(Boolean);
  };

  // Get player display for an event - handles both single player_user_id and multi-owner
  // Returns JSX with color dot(s) so admins can visually distinguish owners.
  const getPlayerDisplayForEvent = (event) => {
    const renderOwner = (user) => (
      <span key={user.id} className="inline-flex items-center gap-1">
        <span
          className="w-3 h-3 rounded-full flex-shrink-0 inline-block"
          style={{ backgroundColor: user.color || '#3b82f6' }}
        />
        <span>{user.name}</span>
      </span>
    );

    // For BONUS events, use the specific player_user_id
    if (event.contestant_id === 'BONUS') {
      if (event.player_user_id) {
        const user = users.find(u => u.id === event.player_user_id);
        return user ? renderOwner(user) : null;
      }
      return null;
    }

    // For regular events, show ALL owners of the contestant (double draft support)
    const owners = getUsersForContestant(event.contestant_id);
    if (owners.length > 0) {
      return (
        <span className="inline-flex items-center gap-1 flex-wrap">
          {owners.map((u, idx) => (
            <span key={u.id} className="inline-flex items-center gap-1">
              {idx > 0 && <span className="text-slate-400">,</span>}
              {renderOwner(u)}
            </span>
          ))}
        </span>
      );
    }

    // Fallback to explicit player_user_id if no roster found
    if (event.player_user_id) {
      const user = users.find(u => u.id === event.player_user_id);
      return user ? renderOwner(user) : null;
    }

    return null;
  };

  // Check if an event is a duplicate (same episode, contestant, and event name)
  const isDuplicateEvent = (event, allEvents) => {
    const duplicates = allEvents.filter(e => 
      e.episode_id === event.episode_id && 
      e.contestant_id === event.contestant_id && 
      e.event_name === event.event_name &&
      e.id !== event.id // Don't count the event itself
    );
    return duplicates.length > 0;
  };

  const addEvent = async () => {
    if (!newEvent.episode_id || !newEvent.contestant_id || !newEvent.event_name) return;
    
    try {
      await authAxios.post(
        `${API}/event-log`,
        {
          season_id: currentSeason.id,
          episode_id: newEvent.episode_id,
          contestant_id: newEvent.contestant_id,
          tribe_id: newEvent.tribe_id || null,
          event_name: newEvent.event_name,
          points: newEvent.points ? parseInt(newEvent.points) : null,
          notes: newEvent.notes || null
        },
        { withCredentials: true }
      );
      toast.success('Event added! Recalculating scores...');
      setShowAddEvent(false);
      setNewEvent({ episode_id: '', user_id: '', contestant_id: '', tribe_id: '', event_name: '', points: '', notes: '' });
      await loadData();
      await recalculateScores();
    } catch (error) {
      console.error('Failed to add event:', error);
      toast.error('Failed to add event');
    }
  };

  const updateEvent = async (eventId, updates) => {
    try {
      // Delete and recreate (since no PATCH endpoint)
      const event = events.find(e => e.id === eventId);
      await authAxios.delete(`${API}/event-log/${eventId}`);
      
      await authAxios.post(
        `${API}/event-log`,
        {
          season_id: currentSeason.id,
          episode_id: updates.episode_id || event.episode_id,
          contestant_id: updates.contestant_id || event.contestant_id,
          tribe_id: updates.tribe_id || event.tribe_id,
          player_user_id: updates.player_user_id || event.player_user_id,
          event_name: updates.event_name || event.event_name,
          points: updates.points !== undefined ? parseInt(updates.points) : event.points,
          notes: updates.notes !== undefined ? updates.notes : event.notes
        },
        { withCredentials: true }
      );
      
      toast.success('Event updated! Recalculating scores...');
      await loadData();
      await recalculateScores();
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error('Failed to update event');
    }
  };

  const addBulkEvent = async () => {
    // Validation based on mode
    if (!bulkEvent.episode_id || !bulkEvent.event_name) return;
    
    if (bulkAddMode === 'tribe' && !bulkEvent.tribe_id) {
      toast.error('Please select a tribe');
      return;
    }
    
    if (bulkAddMode === 'contestants' && selectedContestants.length === 0) {
      toast.error('Please select at least one contestant');
      return;
    }
    
    try {
      let response;
      
      if (bulkAddMode === 'tribe') {
        // Existing tribe-based bulk add
        response = await authAxios.post(
          `${API}/event-log/bulk-tribe`,
          {
            season_id: currentSeason.id,
            episode_id: bulkEvent.episode_id,
            tribe_id: bulkEvent.tribe_id,
            event_name: bulkEvent.event_name,
            notes: bulkEvent.notes || null
          },
          { withCredentials: true }
        );
      } else {
        // New contestant-based bulk add
        response = await authAxios.post(
          `${API}/event-log/bulk-contestants`,
          {
            season_id: currentSeason.id,
            episode_id: bulkEvent.episode_id,
            contestant_ids: selectedContestants,
            event_name: bulkEvent.event_name,
            notes: bulkEvent.notes || null
          },
          { withCredentials: true }
        );
      }
      
      toast.success(`${response.data.count} events added! Recalculating scores...`);
      setShowBulkEvent(false);
      setBulkEvent({ episode_id: '', tribe_id: '', event_name: '', notes: '' });
      setSelectedContestants([]);
      setBulkAddMode('tribe');
      await loadData();
      await recalculateScores();
    } catch (error) {
      console.error('Failed to add bulk event:', error);
      toast.error('Failed to add bulk event');
    }
  };

  const handleDeleteClick = (event) => {
    setEventToDelete(event);
    setShowDeleteDialog(true);
  };

  // CSV Template for Import
  const generateCSVTemplate = () => {
    const headers = ['episode_number', 'contestant_name', 'event_name', 'points', 'notes'];
    const exampleRows = [
      ['1', 'John Smith', 'Individual Immunity Win', '10', 'Won first immunity challenge'],
      ['1', 'Jane Doe', 'Vote Against', '-1', ''],
      ['1', 'Jane Doe', 'Survived Tribal', '2', ''],
      ['2', 'Bob Johnson', 'Found Hidden Idol', '3', 'Found at camp'],
    ];
    
    const csvContent = [headers.join(','), ...exampleRows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'event_log_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Parse CSV file for preview
  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    setImportErrors([]);
    setImportPreview([]);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setImportErrors(['CSV file must have a header row and at least one data row']);
        return;
      }
      
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const requiredHeaders = ['episode_number', 'contestant_name', 'event_name'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        setImportErrors([`Missing required columns: ${missingHeaders.join(', ')}`]);
        return;
      }
      
      const preview = [];
      const errors = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        
        // Validate row
        const rowErrors = [];
        
        // Check episode
        const episodeNum = parseInt(row.episode_number);
        const episode = episodes.find(e => e.episode_number === episodeNum);
        if (!episode) {
          rowErrors.push(`Episode ${row.episode_number} not found`);
        } else {
          row._episode_id = episode.id;
        }
        
        // Check contestant
        const contestant = contestants.find(c => 
          c.full_name.toLowerCase() === row.contestant_name.toLowerCase()
        );
        if (!contestant) {
          rowErrors.push(`Contestant "${row.contestant_name}" not found`);
        } else {
          row._contestant_id = contestant.id;
        }
        
        // Check event name exists in scoring rules (warning only)
        const rule = scoringRules.find(r =>
          (r.event_name || '').toLowerCase() === (row.event_name || '').toLowerCase()
        );
        if (rule) {
          row._points = row.points ? parseInt(row.points) : rule.points;
          row._event_name = rule.event_name; // Use exact case from rules
        } else {
          row._points = row.points ? parseInt(row.points) : 0;
          row._event_name = row.event_name;
          if (!row.points) {
            rowErrors.push(`Event "${row.event_name}" not in scoring rules, points defaulted to 0`);
          }
        }
        
        row._row_number = i + 1;
        row._errors = rowErrors;
        row._valid = rowErrors.length === 0 || rowErrors.every(e => e.includes('not in scoring rules'));
        preview.push(row);
      }
      
      setImportPreview(preview);
      
      const criticalErrors = preview.filter(p => !p._valid);
      if (criticalErrors.length > 0) {
        setImportErrors([`${criticalErrors.length} row(s) have errors that must be fixed`]);
      }
    };
    reader.readAsText(file);
  };

  // Execute import
  const executeImport = async () => {
    const validRows = importPreview.filter(p => p._valid);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }
    
    setImporting(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const row of validRows) {
        try {
          await authAxios.post(
            `${API}/event-log`,
            {
              season_id: currentSeason.id,
              episode_id: row._episode_id,
              contestant_id: row._contestant_id,
              event_name: row._event_name,
              points: row._points,
              notes: row.notes || null
            },
            { withCredentials: true }
          );
          successCount++;
        } catch (error) {
          console.error(`Failed to import row ${row._row_number}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Imported ${successCount} event(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
        await loadData();
        await recalculateScores();
      }
      
      setShowImportDialog(false);
      setImportFile(null);
      setImportPreview([]);
      setImportErrors([]);
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const confirmDelete = async () => {
    if (!eventToDelete) return;

    try {
      await authAxios.delete(`${API}/event-log/${eventToDelete.id}`, {
        withCredentials: true
      });
      
      toast.success('Event deleted! Recalculating scores...');
      setShowDeleteDialog(false);
      setEventToDelete(null);
      await loadData();
      await recalculateScores();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete event');
      setShowDeleteDialog(false);
      setEventToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setEventToDelete(null);
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedEvents.length === filteredEvents.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(filteredEvents.map(e => e.id));
    }
  };

  const toggleSelectEvent = (eventId) => {
    setSelectedEvents(prev =>
      prev.includes(eventId)
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  const handleBulkEditClick = () => {
    if (selectedEvents.length === 0) {
      toast.error('Please select at least one event');
      return;
    }
    setBulkEditData({
      episode_id: '',
      player_user_id: '',
      contestant_id: '',
      tribe_id: '',
      event_name: '',
      points: '',
      notes: ''
    });
    setShowBulkEditDialog(true);
  };

  const applyBulkEdit = async () => {
    // Filter out empty fields
    const updates = Object.entries(bulkEditData).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    if (Object.keys(updates).length === 0) {
      toast.error('Please fill in at least one field to update');
      return;
    }

    try {
      await authAxios.patch(
        `${API}/event-log/bulk`,
        {
          event_ids: selectedEvents,
          updates: updates
        },
        { withCredentials: true }
      );

      toast.success(`Updated ${selectedEvents.length} events! Recalculating scores...`);
      setShowBulkEditDialog(false);
      setSelectedEvents([]);
      await loadData();
      await recalculateScores();
    } catch (err) {
      console.error('Bulk update error:', err);
      toast.error(err.response?.data?.detail || 'Failed to update events');
    }
  };

  const filteredEvents = selectedEpisode === 'all'
    ? events
    : events.filter(e => e.episode_id === selectedEpisode);

  const getContestantName = (id) => {
    if (id === 'BONUS') return 'Bonus';
    return contestants.find(c => c.id === id)?.full_name || 'Unknown';
  };
  const getTribeName = (id) => tribes.find(t => t.id === id)?.name || '-';
  const getEpisodeNumber = (id) => episodes.find(ep => ep.id === id)?.number || '?';
  const getUserName = (id) => users.find(u => u.id === id)?.name || 'Unknown';

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You need admin privileges to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto container-mobile">
        <div className="spacing-section flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Event Log Management
            </h1>
            <p className="text-responsive text-slate-600">Season {currentSeason?.number}</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            {/* Episode Filter */}
            <Select value={selectedEpisode} onValueChange={setSelectedEpisode}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter episode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Episodes</SelectItem>
                {episodes.map(ep => (
                  <SelectItem key={ep.id} value={ep.id}>Episode {ep.number}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Add Event Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Event
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowAddEvent(true)}>
                  Add Single Event
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowBulkEvent(true)}>
                  Bulk Add Events
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowImportDialog(true)}>
                  Import from CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Finalize Episode Button - always visible */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button 
                      onClick={() => setShowFinalizeDialog(true)}
                      className={`w-full sm:w-auto ${
                        selectedEpisode !== 'all' 
                          ? 'bg-emerald-600 hover:bg-emerald-700' 
                          : 'bg-slate-300 cursor-not-allowed'
                      }`}
                      disabled={selectedEpisode === 'all'}
                      data-testid="finalize-episode-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Finalize & Notify
                    </Button>
                  </span>
                </TooltipTrigger>
                {selectedEpisode === 'all' && (
                  <TooltipContent>
                    <p>Select a specific episode to finalize</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            {/* Refresh Button */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={loadData}
              disabled={loading}
              className="w-9 h-9 flex-shrink-0"
              title="Refresh Data"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Missing Player Assignments Warning */}
          {events.filter(e => !e.player_user_id && e.contestant_id !== 'BONUS').length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2 text-amber-800 text-sm">
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>
                  {events.filter(e => !e.player_user_id && e.contestant_id !== 'BONUS').length} events are missing player assignments
                </span>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={backfillPlayerAssignments}
                  className="border-amber-500 text-amber-700 hover:bg-amber-100"
                >
                  Fix & Recalculate
                </Button>
              </div>
            </div>
          )}

          {/* Hint to recalculate if events exist but no player issues */}
          {events.length > 0 && events.filter(e => !e.player_user_id && e.contestant_id !== 'BONUS').length === 0 && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2 text-blue-800 text-sm">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {events.length} events logged. Use "Finalize & Notify" to calculate and publish scores.
                </span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={recalculateScoresOnly}
                className="border-blue-500 text-blue-700 hover:bg-blue-100"
              >
                Recalculate Only
              </Button>
            </div>
          )}
        </div>

        {/* Bulk Edit Actions Bar */}
        {selectedEvents.length > 0 && (
          <div className="mb-4 p-3 md:p-4 bg-indigo-100 border border-indigo-300 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-indigo-900 font-medium text-sm md:text-base">
              {selectedEvents.length} event{selectedEvents.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelectedEvents([])} className="flex-1 sm:flex-none">
                Clear Selection
              </Button>
              <Button size="sm" onClick={handleBulkEditClick} className="flex-1 sm:flex-none">
                Bulk Edit
              </Button>
            </div>
          </div>
        )}

        {/* AI Pending Drafts Panel (Beta) */}
        <AIPendingDraftsPanel
          API={API}
          seasonId={currentSeason?.id}
          selectedEpisodeId={selectedEpisode}
          episodes={episodes}
          scoringRules={scoringRules}
          aiFeatureEnabled={aiFeatureEnabled}
          onApproved={() => { loadData(); }}
        />

        <Card>
          <CardHeader className="padding-card pb-2 md:pb-4 block md:hidden">
            <CardTitle className="heading-card">Event Log</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Swipe left to see all fields →</p>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedEvents.length === filteredEvents.length && filteredEvents.length > 0}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 text-indigo-600 rounded"
                        />
                      </th>
                      <th>Episode</th>
                      <th>Player</th>
                      <th>Contestant</th>
                      <th>Tribe</th>
                      <th>Event</th>
                      <th className="text-center">Points</th>
                      <th>Notes</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-slate-600">
                          No events recorded yet
                        </td>
                      </tr>
                    ) : (
                      filteredEvents.map(event => (
                        <tr key={event.id} className={selectedEvents.includes(event.id) ? 'bg-indigo-50' : ''}>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={selectedEvents.includes(event.id)}
                              onChange={() => toggleSelectEvent(event.id)}
                              className="w-4 h-4 text-indigo-600 rounded"
                            />
                          </td>
                          <td className="font-medium">
                            {editingEvent === event.id ? (
                              <Select
                                defaultValue={event.episode_id}
                                onValueChange={(v) => {
                                  updateEvent(event.id, { episode_id: v });
                                  setEditingEvent(null);
                                }}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {episodes.map(ep => (
                                    <SelectItem key={ep.id} value={ep.id}>Ep {ep.number}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span>Ep {getEpisodeNumber(event.episode_id)}</span>
                            )}
                          </td>
                          <td className="font-medium">
                            {getPlayerDisplayForEvent(event) || '-'}
                          </td>
                          <td>{getContestantName(event.contestant_id)}</td>
                          <td>
                            {event.tribe_id ? (
                              <span 
                                className="px-3 py-1 rounded-full text-white text-xs font-medium"
                                style={{ backgroundColor: tribes.find(t => t.id === event.tribe_id)?.color || '#64748b' }}
                              >
                                {getTribeName(event.tribe_id)}
                              </span>
                            ) : '-'}
                          </td>
                          <td>
                            {editingEvent === event.id ? (
                              <Select
                                defaultValue={event.event_name}
                                onValueChange={(v) => {
                                  updateEvent(event.id, { event_name: v });
                                }}
                              >
                                <SelectTrigger className="w-64">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {[...scoringRules].sort((a, b) => (a.event_name || '').localeCompare(b.event_name || '')).map(rule => (
                                    <SelectItem key={rule.id} value={rule.event_name}>{rule.event_name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-sm">{event.event_name}</span>
                                {isDuplicateEvent(event, events) && (
                                  <span 
                                    className="text-amber-500 cursor-help" 
                                    title={`Warning: Duplicate event detected. This contestant has multiple "${event.event_name}" events for the same episode.`}
                                  >
                                    ⚠️
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="text-center">
                            <Input
                              type="number"
                              defaultValue={event.points}
                              onBlur={(e) => {
                                if (parseInt(e.target.value) !== event.points) {
                                  updateEvent(event.id, { points: e.target.value });
                                }
                              }}
                              className={`w-20 text-center font-bold ${
                                event.points > 0 ? 'text-emerald-600' :
                                event.points < 0 ? 'text-red-600' :
                                'text-slate-600'
                              }`}
                            />
                          </td>
                          <td>
                            <Input
                              defaultValue={event.notes || ''}
                              onBlur={(e) => {
                                if (e.target.value !== event.notes) {
                                  updateEvent(event.id, { notes: e.target.value });
                                }
                              }}
                              placeholder="Add notes..."
                              className="text-sm"
                            />
                          </td>
                          <td className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingEvent(editingEvent === event.id ? null : event.id)}
                                className="h-8 w-8 p-0"
                                title="Edit"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteClick(event)}
                                className="h-8 w-8 p-0"
                                title="Delete Event"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Delete Event
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-slate-600">
              Are you sure you want to delete this event? This action cannot be undone.
            </p>
            
            {eventToDelete && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Episode:</span>
                  <span className="font-medium">Ep {getEpisodeNumber(eventToDelete.episode_id)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Contestant:</span>
                  <span className="font-medium">{getContestantName(eventToDelete.contestant_id)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Event:</span>
                  <span className="font-medium">{eventToDelete.event_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Points:</span>
                  <span className="font-medium">{eventToDelete.points || 0}</span>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={cancelDelete}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedEvents.length} Event{selectedEvents.length > 1 ? 's' : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Fill in only the fields you want to update. Empty fields will be left unchanged.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Episode</Label>
                <Select 
                  value={bulkEditData.episode_id} 
                  onValueChange={(v) => setBulkEditData({...bulkEditData, episode_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Don't change" />
                  </SelectTrigger>
                  <SelectContent>
                    {episodes.map(ep => (
                      <SelectItem key={ep.id} value={ep.id}>Episode {ep.number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Player</Label>
                <Select 
                  value={bulkEditData.player_user_id} 
                  onValueChange={(v) => setBulkEditData({...bulkEditData, player_user_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Don't change" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(None)</SelectItem>
                    {users.filter(u => u.status === 'active' || u.status === 'invited').map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Contestant</Label>
                <Select 
                  value={bulkEditData.contestant_id} 
                  onValueChange={(v) => setBulkEditData({...bulkEditData, contestant_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Don't change" />
                  </SelectTrigger>
                  <SelectContent>
                    {contestants.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tribe</Label>
                <Select 
                  value={bulkEditData.tribe_id} 
                  onValueChange={(v) => setBulkEditData({...bulkEditData, tribe_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Don't change" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(None)</SelectItem>
                    {tribes.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Event Name</Label>
                <Input
                  value={bulkEditData.event_name}
                  onChange={(e) => setBulkEditData({...bulkEditData, event_name: e.target.value})}
                  placeholder="Don't change"
                />
              </div>

              <div>
                <Label>Points</Label>
                <Input
                  type="number"
                  value={bulkEditData.points}
                  onChange={(e) => setBulkEditData({...bulkEditData, points: e.target.value})}
                  placeholder="Don't change"
                />
              </div>

              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={bulkEditData.notes}
                  onChange={(e) => setBulkEditData({...bulkEditData, notes: e.target.value})}
                  placeholder="Don't change"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowBulkEditDialog(false)}
              >
                Cancel
              </Button>
              <Button onClick={applyBulkEdit}>
                Update {selectedEvents.length} Event{selectedEvents.length > 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Single Event Dialog */}
      <Dialog open={showAddEvent} onOpenChange={setShowAddEvent}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Single Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Show inactive contestants toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-2">
                {showInactiveContestants ? (
                  <Eye className="w-4 h-4 text-slate-600" />
                ) : (
                  <EyeOff className="w-4 h-4 text-slate-400" />
                )}
                <Label htmlFor="show-inactive-single" className="text-sm font-medium cursor-pointer">
                  Show non-active contestants
                </Label>
              </div>
              <Checkbox
                id="show-inactive-single"
                checked={showInactiveContestants}
                onCheckedChange={handleShowInactiveToggle}
                data-testid="show-inactive-toggle-single"
              />
            </div>
            
            <div>
              <Label>Episode</Label>
              <Select value={newEvent.episode_id} onValueChange={(v) => setNewEvent({...newEvent, episode_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select episode" />
                </SelectTrigger>
                <SelectContent>
                  {episodes.map(ep => (
                    <SelectItem key={ep.id} value={ep.id}>Episode {ep.number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contestant</Label>
              <Select 
                value={newEvent.contestant_id} 
                onValueChange={(v) => {
                  const contestant = contestants.find(c => c.id === v);
                  const owners = getUsersForContestant(v);
                  // Use first owner for single event, or empty if unassigned
                  const primaryOwner = owners.length > 0 ? owners[0] : null;
                  setNewEvent({
                    ...newEvent, 
                    contestant_id: v,
                    user_id: primaryOwner ? primaryOwner.id : '',
                    tribe_id: contestant?.tribe_id || ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contestant" />
                </SelectTrigger>
                <SelectContent className="min-w-[400px]">
                  {displayedContestants.map(c => {
                    const tribe = tribes.find(t => t.id === c.tribe_id);
                    const owners = getUsersForContestant(c.id);
                    const statusColors = {
                      active: 'bg-emerald-100 text-emerald-700',
                      ftc: 'bg-indigo-100 text-indigo-700',
                      voted_out: 'bg-red-100 text-red-700',
                      removed: 'bg-orange-100 text-orange-700',
                      jury: 'bg-amber-100 text-amber-700',
                      winner: 'bg-purple-100 text-purple-700'
                    };
                    return (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center w-full min-w-[360px]">
                          <div className="flex items-center gap-2 flex-1">
                            <span className="font-medium">{c.full_name}</span>
                            {tribe && (
                              <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: tribe.color }}>
                                {tribe.name}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded text-xs ${statusColors[c.status] || 'bg-slate-100 text-slate-700'}`}>
                              {c.status === 'voted_out' ? 'Out' : 
                               c.status === 'removed' ? 'Removed' :
                               c.status === 'ftc' ? 'FTC' :
                               c.status === 'winner' ? 'Winner' :
                               c.status?.charAt(0).toUpperCase() + c.status?.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 ml-auto">
                            {owners.length > 0 ? (
                              owners.map((owner, idx) => (
                                <div key={owner.id} className="flex items-center gap-1">
                                  {idx > 0 && <span className="text-xs text-slate-400">,</span>}
                                  <div 
                                    className="w-3 h-3 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: owner.color || '#3b82f6' }}
                                  />
                                  <span className="text-xs text-gray-600">{owner.name}</span>
                                </div>
                              ))
                            ) : (
                              <span className="text-xs text-red-500">Unassigned</span>
                            )}
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Event</Label>
              <Select value={newEvent.event_name} onValueChange={(v) => setNewEvent({...newEvent, event_name: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {[...scoringRules].sort((a, b) => (a.event_name || '').localeCompare(b.event_name || '')).map(rule => (
                    <SelectItem key={rule.id} value={rule.event_name}>{rule.event_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Points</Label>
              <Input
                type="number"
                value={newEvent.points}
                onChange={(e) => setNewEvent({...newEvent, points: e.target.value})}
                placeholder="Auto from scoring rule"
              />
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={newEvent.notes}
                onChange={(e) => setNewEvent({...newEvent, notes: e.target.value})}
              />
            </div>
            <Button className="w-full" onClick={addEvent}>Add Single Event</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Event Dialog */}
      <Dialog open={showBulkEvent} onOpenChange={(open) => {
        setShowBulkEvent(open);
        if (!open) {
          setBulkEvent({ episode_id: '', tribe_id: '', event_name: '', notes: '' });
          setSelectedContestants([]);
          setBulkAddMode('tribe');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Add Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Show inactive contestants toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-2">
                {showInactiveContestants ? (
                  <Eye className="w-4 h-4 text-slate-600" />
                ) : (
                  <EyeOff className="w-4 h-4 text-slate-400" />
                )}
                <Label htmlFor="show-inactive-bulk" className="text-sm font-medium cursor-pointer">
                  Show non-active contestants
                </Label>
              </div>
              <Checkbox
                id="show-inactive-bulk"
                checked={showInactiveContestants}
                onCheckedChange={handleShowInactiveToggle}
                data-testid="show-inactive-toggle-bulk"
              />
            </div>
            
            <div>
              <Label>Bulk Add Mode</Label>
              <Select value={bulkAddMode} onValueChange={setBulkAddMode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tribe">By Tribe (All members)</SelectItem>
                  <SelectItem value="contestants">By Contestants (Multi-select)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Episode</Label>
              <Select value={bulkEvent.episode_id} onValueChange={(v) => setBulkEvent({...bulkEvent, episode_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select episode" />
                </SelectTrigger>
                <SelectContent>
                  {episodes.map(ep => (
                    <SelectItem key={ep.id} value={ep.id}>Episode {ep.number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {bulkAddMode === 'tribe' && (
              <div>
                <Label>Tribe</Label>
                <Select value={bulkEvent.tribe_id} onValueChange={(v) => setBulkEvent({...bulkEvent, tribe_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tribe" />
                  </SelectTrigger>
                  <SelectContent>
                    {displayedTribes.map(tribe => {
                      const tribeContestants = contestants.filter(c => c.tribe_id === tribe.id);
                      const activeCount = tribeContestants.filter(c => !inactiveStatuses.includes(c.status)).length;
                      const totalCount = tribeContestants.length;
                      return (
                        <SelectItem key={tribe.id} value={tribe.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: tribe.color }}
                            />
                            <span>{tribe.name}</span>
                            <span className="text-xs text-slate-500">
                              ({showInactiveContestants ? totalCount : activeCount} member{(showInactiveContestants ? totalCount : activeCount) !== 1 ? 's' : ''})
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {bulkAddMode === 'contestants' && (
              <div>
                <Label>Select Contestants</Label>
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  <div className="flex items-center space-x-2 p-2 border-b">
                    <Checkbox
                      id="select-all-bulk"
                      checked={selectedContestants.length === displayedContestants.length && displayedContestants.length > 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedContestants(displayedContestants.map(c => c.id));
                        } else {
                          setSelectedContestants([]);
                        }
                      }}
                    />
                    <label htmlFor="select-all-bulk" className="font-medium cursor-pointer">
                      Select All ({displayedContestants.length})
                    </label>
                  </div>
                  {displayedContestants.map(contestant => {
                    const tribe = tribes.find(t => t.id === contestant.tribe_id);
                    const owners = getUsersForContestant(contestant.id);
                    const statusColors = {
                      active: 'bg-emerald-100 text-emerald-700',
                      voted_out: 'bg-red-100 text-red-700',
                      removed: 'bg-orange-100 text-orange-700',
                      jury: 'bg-amber-100 text-amber-700',
                      winner: 'bg-purple-100 text-purple-700'
                    };
                    return (
                      <div key={contestant.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                        <Checkbox
                          id={`bulk-contestant-${contestant.id}`}
                          checked={selectedContestants.includes(contestant.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedContestants(prev => [...prev, contestant.id]);
                            } else {
                              setSelectedContestants(prev => prev.filter(id => id !== contestant.id));
                            }
                          }}
                        />
                        <label 
                          htmlFor={`bulk-contestant-${contestant.id}`} 
                          className="cursor-pointer flex-1 text-sm"
                        >
                          <div className="flex items-center justify-between w-full gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{contestant.full_name}</span>
                              {tribe && (
                                <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: tribe.color }}>
                                  {tribe.name}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded text-xs ${statusColors[contestant.status] || 'bg-slate-100 text-slate-700'}`}>
                                {contestant.status === 'voted_out' ? 'Out' : 
                                 contestant.status === 'removed' ? 'Removed' :
                                 contestant.status === 'winner' ? 'Winner' :
                                 contestant.status?.charAt(0).toUpperCase() + contestant.status?.slice(1)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {owners.length > 0 ? (
                                owners.map((owner, idx) => (
                                  <div key={owner.id} className="flex items-center gap-1">
                                    {idx > 0 && <span className="text-xs text-slate-400">,</span>}
                                    <div 
                                      className="w-3 h-3 rounded-full flex-shrink-0" 
                                      style={{ backgroundColor: owner.color || '#3b82f6' }}
                                    />
                                    <span className="text-xs text-gray-600">{owner.name}</span>
                                  </div>
                                ))
                              ) : (
                                <span className="text-xs text-red-500">Unassigned</span>
                              )}
                            </div>
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedContestants.length} contestant{selectedContestants.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            )}

            <div>
              <Label>Event</Label>
              <Select value={bulkEvent.event_name} onValueChange={(v) => setBulkEvent({...bulkEvent, event_name: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {[...scoringRules].sort((a, b) => (a.event_name || '').localeCompare(b.event_name || '')).map(rule => (
                    <SelectItem key={rule.id} value={rule.event_name}>{rule.event_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={bulkEvent.notes}
                onChange={(e) => setBulkEvent({...bulkEvent, notes: e.target.value})}
              />
            </div>
            <Button className="w-full" onClick={addBulkEvent}>
              {bulkAddMode === 'tribe' 
                ? 'Add for All Tribe Members' 
                : `Add for ${selectedContestants.length} Selected Contestant${selectedContestants.length !== 1 ? 's' : ''}`
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finalize Episode Dialog */}
      <Dialog open={showFinalizeDialog} onOpenChange={setShowFinalizeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Finalize Episode {episodes.find(e => e.id === selectedEpisode)?.number} Scores
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              This will recalculate all scores and send email notifications to all players with their updated standings.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>What happens:</strong>
              </p>
              <ul className="text-sm text-amber-700 mt-1 ml-4 list-disc">
                <li>All scores for this season will be recalculated</li>
                <li>Leaderboard will be updated</li>
                <li>All players will receive an email with their episode points and rank</li>
              </ul>
            </div>
            <p className="text-sm text-slate-600">
              Make sure you have finished logging all events for Episode {episodes.find(e => e.id === selectedEpisode)?.number} before finalizing.
            </p>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowFinalizeDialog(false)}
              disabled={finalizing}
            >
              Cancel
            </Button>
            <Button 
              onClick={finalizeEpisodeScores}
              disabled={finalizing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-finalize-btn"
            >
              {finalizing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Finalizing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Finalize & Send Emails
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Events Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        setShowImportDialog(open);
        if (!open) {
          setImportFile(null);
          setImportPreview([]);
          setImportErrors([]);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Events from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import scoring events.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Template Download */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">CSV Template</h4>
              <p className="text-sm text-blue-700 mb-3">
                Download the template to see the required format. Required columns:
              </p>
              <ul className="text-sm text-blue-700 mb-3 ml-4 list-disc">
                <li><strong>episode_number</strong> - Episode number (e.g., 1, 2, 3)</li>
                <li><strong>contestant_name</strong> - Full name of the contestant</li>
                <li><strong>event_name</strong> - Event type (should match your scoring rules)</li>
                <li><strong>points</strong> - (Optional) Auto-assigned from scoring rules if not provided</li>
                <li><strong>notes</strong> - (Optional) Additional notes</li>
              </ul>
              <p className="text-sm text-blue-700 mb-3">
                <strong>Note:</strong> Player(s) will be auto-assigned based on roster assignments for each contestant.
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={generateCSVTemplate}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                Download Template
              </Button>
            </div>

            {/* File Upload */}
            <div>
              <Label htmlFor="importFile">Upload CSV File</Label>
              <Input
                id="importFile"
                type="file"
                accept=".csv"
                onChange={handleImportFileChange}
                className="mt-1"
              />
            </div>

            {/* Errors */}
            {importErrors.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="font-semibold text-red-800 mb-1">Errors Found</p>
                <ul className="text-sm text-red-700 list-disc ml-4">
                  {importErrors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Preview Table */}
            {importPreview.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">
                  Preview ({importPreview.filter(p => p._valid).length} valid, {importPreview.filter(p => !p._valid).length} invalid)
                </h4>
                <div className="overflow-x-auto max-h-64 border rounded-lg">
                  <table className="data-table text-sm">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Episode</th>
                        <th>Contestant</th>
                        <th>Event</th>
                        <th>Points</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((row, idx) => (
                        <tr key={idx} className={row._valid ? '' : 'bg-red-50'}>
                          <td>{row._row_number}</td>
                          <td>{row.episode_number}</td>
                          <td>{row.contestant_name}</td>
                          <td>{row.event_name}</td>
                          <td>{row._points}</td>
                          <td>
                            {row._valid ? (
                              <span className="text-green-600 text-xs">Valid</span>
                            ) : (
                              <span className="text-red-600 text-xs" title={row._errors.join(', ')}>
                                {row._errors[0]}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Scoring Rules Reference */}
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-800">
                View Available Event Names (from Scoring Rules)
              </summary>
              <div className="mt-2 p-3 bg-slate-50 rounded-lg max-h-40 overflow-y-auto">
                <div className="grid grid-cols-2 gap-1">
                  {scoringRules.map(rule => (
                    <div key={rule.id} className="flex justify-between text-xs">
                      <span>{rule.event_name}</span>
                      <span className="text-slate-500">{rule.points > 0 ? '+' : ''}{rule.points}</span>
                    </div>
                  ))}
                </div>
              </div>
            </details>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowImportDialog(false);
                setImportFile(null);
                setImportPreview([]);
                setImportErrors([]);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={executeImport}
              disabled={importing || importPreview.filter(p => p._valid).length === 0}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {importing ? 'Importing...' : `Import ${importPreview.filter(p => p._valid).length} Event(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
